import { Campaign, Programme, TherapeuticArea, Wave } from "app/models/programmes/Programme";
import { ProgrammeService } from "app/services/programme/ProgrammeService";
import { useEffect, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import { useProgrammeExplorerSupporter } from "../ProgrammeExplorerSupporterProvider";
import toastr from "toastr";
import { DocumentService } from "app/services/document/DocumentService";
import { ConnectService } from "app/services/connect/ConnectService";
import { RangeDate } from "@components/form/CustomDateRangePicker";
import moment from "moment";
import { ProgrammeMediaService } from "app/services/programmeMedia/ProgrammeMediaService";


const useProgrammeExplorerHeader = () => {

    const [selectedProjectCode, setSelectedProjectCode] = useState<string | null>(null);
    const [projectCodes, setProjectCodes] = useState<string[]>([]);

    const [betweenDates, setBetweenDates] = useState<any>(null);

    const targetOptions = [
        { value: 'overall', label: 'Overall' },
        { value: 'connect', label: 'Connect' },
        { value: 'project', label: 'Project (Single media)' },
        { value: 'group', label: 'Group (Multimedia page)' },
    ];

    const [isOpened, setIsOpened] = useState<boolean>(false);

    const [programmes, setProgrammes] = useState<Programme[]>([]);
    const [connects, setConnects] = useState<any>([]);
    const [groupNames, setGroupNames] = useState<any>([]);
    const [selectedGroups, setSelectedGroups] = useState<any>([]);
    const [selectedprogrammeMedia, setSelectedprogrammeMedia] = useState<any>([]);
    const [programmeMedias, setprogrammeMedias] = useState<any>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedProgrammes, setSelectedProgrammes] = useState<Programme[]>([]);

    const [selectedWaves, setSelectedWaves] = useState<MultiValue<unknown>>([]);
    const [selectedConnects, setSelectedConnects] = useState<any>([]);
    const [waves, setWaves] = useState<Wave[]>([]);
    const [target, setTarget] = useState<string | null>(null);
    const [waveOptions, setWaveOptions] = useState<MultiValue<unknown>>([]);

    const ProgrammeExplorer = useProgrammeExplorerSupporter();
    const [pdfFile, setPdfFile] = useState<any>(null)
    const [pptFile, setPptFile] = useState<any>(null)
    const [showPdf, setShowPdf] = useState<boolean>(false)
    const [showPpt, setShowPpt] = useState<boolean>(false)
    const [lastPptExportFilename, setLastPptExportFilename] = useState<string>('COR2ED_The_Heart_of_Medical_Education')

    // Metric Selection Modal State
    const [currentFilters, setCurrentFilters] = useState<any>(null);
    const [showMetricModal, setShowMetricModal] = useState<boolean>(false);
    const [metricModalType, setMetricModalType] = useState<'pdf' | 'ppt'>('ppt');
    const [isGenerating, setIsGenerating] = useState<boolean>(false);

    useEffect(() => {
        getConnects()
    }, []);

    useEffect(() => {
        if (target === 'group') {
            resetProgrammes()
            resetWaves()
            resetGroups()
            resetProgrammeMedias()
            setBetweenDates({
                from: moment().subtract(1, 'year').format('YYYY-MM-DD'),
                to: moment().format('YYYY-MM-DD')
            })
            if (selectedProjectCode !== null) {
                getGroups(selectedProjectCode)
            }
        } else if (target === 'project') {
            resetGroups()
            resetProgrammeMedias()
            resetProgrammes()
            resetWaves()
            setBetweenDates(null)
            if (selectedProjectCode !== null) {
                getProgrammesByProjectCode(selectedProjectCode)
            }
        }
    }, [selectedProjectCode, target]);

    useEffect(() => {
        resetWaves()
        if (programmes.length > 0) {
            getProgrammeDates(programmes);
        }
    }, [programmes])

    useEffect(() => {
        resetProgrammeMedias()
    }, [groupNames])

    useEffect(() => {
        if (selectedProgrammes.length === 1) {
            getProgrammeWaves(selectedProgrammes[0]);
        } else {
            setWaves([]);
            setWaveOptions([])
        }
    }, [selectedProgrammes])

    useEffect(() => {
        setWaveOptions([]);
        if (waves.length > 0) {
            formatWaveOptions(waves);
        }

    }, [waves])


    const resetProgrammes = () => {
        setProgrammes([])
        setSelectedProgrammes([])
    }

    const resetWaves = () => {
        setWaves([])
        setSelectedWaves([])
    }

    const resetGroups = () => {
        setGroupNames([])
        setSelectedGroups([])
    }

    const resetProgrammeMedias = () => {
        setprogrammeMedias([])
        setSelectedprogrammeMedia([])
    }

    const onChangeWaves = (selected: MultiValue<unknown>): void => {
        setSelectedWaves(selected);
    }

    const getProgrammesByProjectCode = async (projectCode: string) => {
        setLoading(true);
        let response = (await (new ProgrammeService().search(
            {
                filter_filters: {
                    project_code: projectCode,
                }
            }
        ))).getResponseData();

        if (response.success === false) return;

        setProgrammes(response.data);
        ProgrammeExplorer.updateProgrammeExplorerProgrammes(response.data);
        let searchTypeElearning = response.data.filter((programme: Programme) => programme.type === 'e-learning' || programme.type === 'micro-learning')

        if (searchTypeElearning.length > 0) {
            ProgrammeExplorer.updateIsElearning(true);
        } else {
            ProgrammeExplorer.updateIsElearning(false);
        }
        setLoading(false);
    }

    const getProgrammeValues = (): MultiValue<unknown> => {
        return programmes.map((programme: Programme) => {
            return {
                value: programme.id,
                label: `${programme.projectCode}: ${programme.name} [${programme.cmsId}]`
            }
        })
    }


    /**
     * When user select specific programmes, we need to recover the waves only if the user select one.
     */
    const onChangeProgrammes = (newValue: MultiValue<unknown>) => {
        setLoading(true);
        try {
            let programmeValues = newValue.map((value: any) => value.value);
            let programmesToFilter = programmes.filter((programme: Programme) => programmeValues.includes(programme.id));

            // If theres not programmes selected we check the programmes and if theres programmes saved in state we filter them
            if (programmeValues.length === 0 && programmes.length > 0) {
                programmesToFilter = programmes.map((programme: Programme) => programme);
            }

            setSelectedProgrammes(programmesToFilter);

            let firstPublicationDate = moment(programmesToFilter.sort((a: Programme, b: Programme) => {
                return moment(a.publishedAt?.date).diff(moment(b.publishedAt?.date))
            })[0].publishedAt.date).format('YYYY-MM-DD');

            setBetweenDates({
                from: firstPublicationDate,
                to: moment().format('YYYY-MM-DD')
            });
        } catch (error) {
            console.error("Error selecting programmes", error);
        } finally {
            setLoading(false);
        }
    }



    // Get defined date from y date to of programmes
    const getProgrammeDates = async (programmes: Programme[]) => {
        let response = (await (new ProgrammeService().searchDatesBetweenProgrammes({
            filter_filters: {
                programmes: programmes.map((programme: Programme) => programme.id),
            },
        }))).getResponseData();

        if (response == undefined || response?.success === false) {
            toastr.warning("Error with project code selected: " + response ? response?.message : "No response from server");
            setBetweenDates({
                from: moment().startOf('month').format('YYYY-MM-DD'),
                to: moment().endOf('month').format('YYYY-MM-DD')
            });
            return
        };

        const { from, to } = response.data;

        setBetweenDates({
            from,
            to
        });
    }

    const getProjectCodes = async () => {
        let response = (await (new ProgrammeService().searchProjectCode({
            filter_filters: {
                search: "",
            },
        }))).getResponseData();

        if (response.success === false) return;

        let projectCodeOptions = response.data.map((projectCode: string) => {
            return {
                value: projectCode,
                label: projectCode
            }
        });

        setProjectCodes(projectCodeOptions);

    }


    const onChangeProgrammeProjectCode = (newValue: SingleValue<unknown>) => {
        // Recover the project code selected value and update the status
        let programmeSelected: any = newValue
        setSelectedProjectCode(programmeSelected.value);
        setPdfFile(null);
        setPptFile(null);
        setShowPdf(false);
        setShowPpt(false);
    }

    const onFixedToggle = (fixed: boolean) => {
        const header = document.getElementById('kt_header');

        if (fixed === true) {
            if (!header?.classList.contains('header-to-back')) {
                header?.classList.add('header-to-back');
            }
        }

        if (fixed === false) {
            if (header?.classList.contains('header-to-back')) {
                header?.classList.remove('header-to-back');
            }
        }
    }

    const getProgrammeWaves = async (programme: Programme) => {
        let waves = programme.campaigns.reduce((accumulator: Wave[], currentValue: Campaign) => {

            let indexInAccumulator = accumulator.findIndex((wave: Wave) => wave.name === currentValue.wave);

            if (indexInAccumulator !== -1) {
                accumulator[indexInAccumulator].campaigns.push(currentValue);
                return accumulator;
            }

            let newWave: Wave = {
                name: currentValue.wave,
                label: `${currentValue.code}-${currentValue.wave}`,
                value: currentValue.wave,
                code: currentValue.code,
                campaigns: [currentValue]
            }

            return [...accumulator, newWave];
        }, []);

        setWaves(waves)
    }

    const formatWaveOptions = (waves: Wave[]) => {
        let waveOptions: MultiValue<unknown> = waves.map((wave: Wave) => {
            return {
                value: wave.campaigns.map((campaign: Campaign) => campaign.id),
                label: wave.label
            }
        })
        setWaveOptions(waveOptions);
    }

    const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '').trim();

    const resolveProgrammeTitle = (): string => {
        if (target === 'group') {
            const selectedMediaOptions = selectedprogrammeMedia.length > 0
                ? programmeMedias.filter((media: any) => selectedprogrammeMedia.includes(media.value))
                : programmeMedias;

            const names = Array.from(new Set(
                selectedMediaOptions
                    .map((media: any) => stripHtml(media.programmeName || ''))
                    .filter(Boolean)
            )) as string[];

            if (names.length === 1) {
                return names[0];
            }
            if (names.length > 1) {
                return `${names[0]} and others`;
            }
        }

        if (target === 'project') {
            const programmesToTitle = selectedProgrammes.length > 0 ? selectedProgrammes : programmes;
            if (programmesToTitle.length === 1) {
                return programmesToTitle[0].name;
            }
            if (programmesToTitle.length > 1) {
                return `${programmesToTitle[0].name} and others`;
            }
        }

        return selectedProjectCode || 'PROGRAMME TITLE';
    };

    const applyFilters = () => {

        let apllyFilters;

        if (target === 'project') {

            if (!selectedProjectCode) {
                toastr.warning("Please select a project code");
                return
            }

            let programmesToFilter: Programme[]


            programmesToFilter = programmes.map((programme: Programme) => programme)

            if(selectedProgrammes.length > 0) {
                programmesToFilter = selectedProgrammes.map((programme: Programme) => programme)
            }

            // Filter therapeutic areas of programme filtered
            let allTherapeuticAreasToFilter: number[] = programmesToFilter.reduce(
                (accumulator: number[], currentValue: Programme) => {
                    let therapeuticAreaIds = currentValue.therapeuticAreas.map((therapeuticArea: TherapeuticArea) => therapeuticArea.therapeutic_area_id)
                    return [...accumulator, ...therapeuticAreaIds]
                }, []
            );

            // reduce the array to unique values
            let therapeuticAreasToFilter = allTherapeuticAreasToFilter.reduce((accumulator: number[], currentValue: number) => {
                if (accumulator.includes(currentValue))
                    return accumulator

                return [...accumulator, currentValue]
            }, []);

            let campaignGroups = selectedWaves.map((wave: any) => wave.value)
            let campaignIds = campaignGroups.reduce((accumulator: number[], currentValue: number[]) => {
                return [...accumulator, ...currentValue]
            }, []);


            apllyFilters = {
                programmes: programmesToFilter.map((programme: Programme) => programme.id),
                connects: [],
                therapeutic_areas: therapeuticAreasToFilter,
                campaigns: campaignIds,
                // Update the fiter with selected campaigns realted to selected wave.
                between_dates: betweenDates,
                programme_title: resolveProgrammeTitle(),
            };
        } else if (target === 'connect') {

            if (selectedConnects === null || selectedConnects.length === 0) {
                toastr.warning("Please select one or more connects");
                return
            }
            
            apllyFilters = {
                connects: selectedConnects.map((connect: any) => connect),
                programmes: [],
                campaigns: [],
                therapeutic_areas: [],
                group_name_media: [],
                name_media: [],
                between_dates: betweenDates,
                programme_title: resolveProgrammeTitle(),
            }
        } else if (target === 'group') {
            
            if (!selectedProjectCode) {
                toastr.warning("Please select a project code");
                return
            }

            if (!selectedGroups || selectedGroups.length === 0) {
                toastr.warning("Please select at least one group");
                return
            }

            apllyFilters = {
                programmes: [],
                campaigns: [],
                therapeutic_areas: [],
                connects: [],
                project_code_media: selectedProjectCode,
                group_name_media: selectedGroups,
                name_media: selectedprogrammeMedia,
                between_dates: betweenDates,
                programme_title: resolveProgrammeTitle(),
            }
        
        } else {
            apllyFilters = {
                programmes: [],
                campaigns: [],
                therapeutic_areas: [],
                connects: [],
                group_name_media: [],
                name_media: [],
                between_dates: betweenDates,
                programme_title: resolveProgrammeTitle(),
            }
        }
        ProgrammeExplorer.resetExportData();
        ProgrammeExplorer.resetMetricTotals();
        ProgrammeExplorer.updateFilters({
            ...apllyFilters
        });

        setCurrentFilters(apllyFilters);
        // Reset files when filters change
        setPdfFile(null);
        setPptFile(null);
        setShowPdf(false);
        setShowPpt(false);
    }

    /**
     * This function downloads a document stored in the state of its respective ppt or pdf document type
     * @param type file type 'pdf' or 'ppt' 
     * @param filename name of file to download
     */
    const downloadFileCache = async (type: string, filename: string | null = null) => {
        const file = type === 'pdf' ? pdfFile : pptFile;
        const resolvedFilename = filename ?? (type === 'ppt' ? lastPptExportFilename : 'COR2ED_The_Heart_of_Medical_Education');
        const urlDownloadFile = window.URL.createObjectURL(new Blob([file]))
        const link = document.createElement('a');
        link.href = urlDownloadFile;
        link.setAttribute('download', `${resolvedFilename}.${type}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link)
    }

    const getConnects = async () => {
        let response = (await (new ConnectService().search({ filter_order: [{ field: 'name', order: 'asc' }] }))).getResponseData();
        if (response.success) {
            // parse connect to select
            let connects = response.data.map((element: any) => ({ value: element.id, label: element.name }))
            setConnects(connects);
        } else {
            console.error("Error getting available connects.")
        }
    }

    const getGroups = async (projectCode: string) => {
        setLoading(true);
        let response = (await (new ProgrammeMediaService().getGroupsByProjectCode(projectCode))).getResponseData();

        if (response.success === false) return;

        let groupNames = response.data.map((group: any) => {
            return {
                value: group.group_name,
                label: group.group_name
            }
        });

        setGroupNames(groupNames);
        setLoading(false);
    }

    const onChangeGroups = (newValue: MultiValue<unknown>) => {
        let groups = newValue.map((value: any) => value.value);
        setSelectedGroups(groups as never[]);
        if (groups.length === 0) {
            setprogrammeMedias([])
        }
        getProgrammeMedias(groups, selectedProjectCode)
    }

    const getProgrammeMedias = async ( group: any, projectCode: string | null) => {
        setLoading(true);

        let response = (await (new ProgrammeMediaService().getProgrammeMediasByGroupAndProjectCode(projectCode,group))).getResponseData();

        if (response.success === false) return;

        let programmeMediaOptions = response.data.map((programmeMedia: any) => {
            const mediaName = stripHtml(programmeMedia.name || '');
            const programmeName = stripHtml(programmeMedia.programme_name || '');
            return {
                value: programmeMedia.name,
                label: programmeName + ' - ' + mediaName,
                programmeName,
            }
        });

        setprogrammeMedias(programmeMediaOptions);

        setLoading(false);
    }

    const onChangeProgrammeMedias = (newValue: MultiValue<unknown>) => {
        let programmeMedias = newValue.map((value: any) => value.value);
        setSelectedprogrammeMedia(programmeMedias as never[]);
    }

    const getProjectCodesMedia = async () => {
        let response = (await (new ProgrammeMediaService().getProjectCodes())).getResponseData();

        if (response.success === false) return;

        let projectCodeOptions = response.data.map((projectCode: any) => {
            return {
                value: projectCode.project_code,
                label: projectCode.project_code
            }
        });

        setProjectCodes(projectCodeOptions);
    }

    const onChangeDate = (range: RangeDate) => {
        if (range.startDate && range.endDate) {
            setBetweenDates({
                from: range.startDate,
                to: range.endDate
            })
        }
    }

    const onChangeTarget = (newValue: SingleValue<unknown>) => {
        let targetSelected: any = newValue
        setTarget(targetSelected.value);
        setCurrentFilters(null);
        setPdfFile(null);
        setPptFile(null);
        setShowPdf(false);
        setShowPpt(false);
        if (target == null || target === '') {
            setBetweenDates({
                from: moment().startOf('month').format('YYYY-MM-DD'),
                to: moment().endOf('day').format('YYYY-MM-DD')
            });
        }
        if (targetSelected.value === 'group') {
            getProjectCodesMedia()
        } else {
            getProjectCodes()
        }
    }

    const onChangeConnects = (newValue: MultiValue<unknown>) => {
        let connects = newValue.map((value: any) => value.value);
        setSelectedConnects(connects as never[]);
    }

    const statusDownload = (type: string) => type === 'pdf' ? showPdf : showPpt;

    const openMetricModal = (type: 'pdf' | 'ppt') => {
        if (!currentFilters) {
             toastr.warning("Please apply filters first");
             return;
        }
        setMetricModalType(type);
        setShowMetricModal(true);
    }

    const waitForExportImages = async (maxMs: number = 12000, intervalMs: number = 200): Promise<Record<string, string>> => {
        const start = Date.now();
        return new Promise((resolve) => {
            const tick = () => {
                const images = ProgrammeExplorer.exportImages || {};
                const imagesReady = !!(images['geo_map'] && images['specialties_pie']);
                if (imagesReady || (Date.now() - start >= maxMs)) {
                    resolve(images);
                } else {
                    setTimeout(tick, intervalMs);
                }
            };
            tick();
        });
    };

    const waitForExportInsights = async (maxMs: number = 12000, intervalMs: number = 200): Promise<Record<string, any>> => {
        const start = Date.now();
        return new Promise((resolve) => {
            const tick = () => {
                const insights = ProgrammeExplorer.exportInsights || {};
                const cardsReady = ProgrammeExplorer.exportInsightsCardsReady;
                const hasCardKeys =
                    Array.isArray(insights.website_sources) &&
                    Array.isArray(insights.video_sources) &&
                    Array.isArray(insights.file_types);
                try {
                    console.log('[Export] waitForExportInsights tick cardsReady:', cardsReady, 'hasCardKeys:', hasCardKeys, 'elapsed:', Date.now() - start, 'ms');
                } catch {}
                if ((cardsReady && hasCardKeys) || (Date.now() - start >= maxMs)) {
                    try {
                        console.log('[Export] waitForExportInsights resolved. keys:', Object.keys(insights))
                    } catch {}
                    resolve(insights);
                } else {
                    setTimeout(tick, intervalMs);
                }
            };
            tick();
        });
    };

    const downloadBlob = (blob: Blob, type: string, filenameBase: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filenameBase}.${type}`);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
    }

    const resolveProgrammesForExport = (): Programme[] => {
        if (selectedProgrammes.length > 0) {
            return selectedProgrammes;
        }

        const programmeIds: number[] = currentFilters?.programmes ?? [];
        if (programmeIds.length === 0) {
            return [];
        }

        return programmes.filter((programme: Programme) => programmeIds.includes(programme.id));
    };

    const resolvePptExportType = (): string | undefined => {
        const programmesForExport = resolveProgrammesForExport();
        if (programmesForExport.length === 0) {
            return undefined;
        }

        const useMedicalAffairs = programmesForExport.every(
            (programme: Programme) => programme.medicalAffairs === true
        );

        return useMedicalAffairs ? 'medical-affairs' : undefined;
    };

    const PPT_EXPORT_FILENAME_COR2ED = 'COR2ED_The_Heart_of_Medical_Education';
    const PPT_EXPORT_FILENAME_MEDICAL_AFFAIRS = 'Medical_Affairs';

    const resolvePptExportFilename = (): string => {
        return resolvePptExportType() === 'medical-affairs'
            ? PPT_EXPORT_FILENAME_MEDICAL_AFFAIRS
            : PPT_EXPORT_FILENAME_COR2ED;
    };

    const handleGenerateWithMetrics = async (selectedMetrics: string[]) => {
        setShowMetricModal(false);
        setIsGenerating(true);
        
        try {
            // Ensure metrics is an array and filter out empty values if any
            const metricsArray = Array.isArray(selectedMetrics) ? selectedMetrics : [];

            const [images, insights] = await Promise.all([
                waitForExportImages(),
                waitForExportInsights(),
            ]);
            try {
                console.log('[Export] handleGenerateWithMetrics images to send keys:', Object.keys(images))
                console.log('[Export] handleGenerateWithMetrics insights to send keys:', Object.keys(insights))
            } catch {}

            const pptExportType = metricModalType === 'ppt' ? resolvePptExportType() : undefined;

            let filtersPayload = {
                filter_filters: {
                    ...currentFilters,
                    metrics: metricsArray,
                    metric_values: ProgrammeExplorer.metricTotals,
                    programme_title: currentFilters.programme_title || resolveProgrammeTitle(),
                    // If campaigns are selected, we shouldn't send programmes
                    programmes: (currentFilters.campaigns && currentFilters.campaigns.length > 0) ? [] : currentFilters.programmes,
                    export_images: images,
                    export_insights: insights,
                    ...(pptExportType ? { type: pptExportType } : {}),
                }
            };

            // Ensure therapeutic_areas is empty as per original logic
            filtersPayload.filter_filters.therapeutic_areas = [];

            const file = await (new DocumentService().downloadFile(metricModalType, filtersPayload));
            const exportFilename = metricModalType === 'pdf'
                ? PPT_EXPORT_FILENAME_COR2ED
                : resolvePptExportFilename();
            
            if (metricModalType === 'pdf') {
                setPdfFile(file);
                setShowPdf(true);
                downloadBlob(new Blob([file]), 'pdf', exportFilename);
            } else {
                setLastPptExportFilename(exportFilename);
                setPptFile(file);
                setShowPpt(true);
                downloadBlob(new Blob([file]), 'ppt', exportFilename);
            }
        } catch (error) {
            console.error("Error generating file", error);
            toastr.error("Error generating file");
        } finally {
            setIsGenerating(false);
        }
    }

    const areAllTotalsLoaded = Object.keys(ProgrammeExplorer.metricTotals).length > 0 
        && Object.values(ProgrammeExplorer.metricTotals).every(val => val !== null);

    const canShowExportButtons = !!currentFilters;
    const isExportImagesReady = !!(
        ProgrammeExplorer.exportImages &&
        ProgrammeExplorer.exportImages['geo_map'] &&
        ProgrammeExplorer.exportImages['specialties_pie']
    );
    const isExportInsightsReady = ProgrammeExplorer.exportInsightsCardsReady;
    const isExportReady = isExportImagesReady && isExportInsightsReady;

    return {
        isOpened,
        onChangeProgrammeProjectCode,
        programmes,
        betweenDates,
        selectedProjectCode,
        getProgrammeValues,
        onChangeProgrammes,
        selectedProgrammes,
        waveOptions,
        applyFilters,
        projectCodes,
        onChangeWaves,
        selectedWaves,
        onFixedToggle,
        downloadFileCache,
        statusDownload,
        onChangeTarget,
        target,
        targetOptions,
        loading,
        onChangeDate,
        setIsOpened,
        connects,
        onChangeConnects,
        onChangeGroups,
        onChangeProgrammeMedias,
        groupNames,
        programmeMedias,
        // New exports
        currentFilters,
        showMetricModal,
        setShowMetricModal,
        metricModalType,
        openMetricModal,
        handleGenerateWithMetrics,
        isGenerating,
        areAllTotalsLoaded,
        isExportImagesReady,
        canShowExportButtons,
        isExportInsightsReady,
        isExportReady
    }
}


export default useProgrammeExplorerHeader;
